import React, { createContext, useContext, useEffect, useState } from "react";
import { LanguageKey } from "../types/internacionalization";
import { useSelector } from "react-redux";
import { AppStore } from "../redux/store";

interface LanguageContextProps {
  language: LanguageKey;
  setLanguage: React.Dispatch<React.SetStateAction<LanguageKey>>;
}

const LanguageContext = createContext<LanguageContextProps | undefined>(
  undefined
);

const STORAGE_KEY = "languageSelected";

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const user = useSelector((store: AppStore) => store.user);

  // Prioriza user.language (DB, fuente de verdad); cae a localStorage solo
  // como cache previo a la hidratación del usuario, y por último a "es".
  const getInitialLanguage = (): LanguageKey => {
    if (user?.language && ["es", "en", "pt", "ca"].includes(user.language))
      return user.language as LanguageKey;
    const stored = localStorage.getItem(STORAGE_KEY) as LanguageKey | null;
    if (stored && ["es", "en", "pt", "ca"].includes(stored)) return stored;
    return "es";
  };

  const [language, setLanguage] = useState<LanguageKey>(getInitialLanguage);

  useEffect(() => {
    const localeMap: Record<LanguageKey, string> = {
      en: "en-US",
      es: "es-AR",
      pt: "pt-BR",
      ca: "ca-ES",
    };

    if (typeof document === "undefined") return;
    const nextLang = localeMap[language] ?? "en-US";
    document.documentElement.lang = nextLang;
  }, [language]);

  // La DB es la fuente de verdad: al hidratarse/cambiar el usuario en redux,
  // adoptamos su idioma. La persistencia de un cambio real la hace el selector
  // de idioma (acción del usuario), no este contexto.
  useEffect(() => {
    if (user?.language && user.language !== language) {
      setLanguage(user.language as LanguageKey);
    }
  }, [user?.language]);

  // Cache local del idioma activo (evita el flash previo a la hidratación)
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context)
    throw new Error("useLanguage must be used within a LanguageProvider");
  return context;
};